// **************** common import *******************
import React, { useState, useEffect } from "react";
import { useParams } from 'react-router-dom';
import { getNotesById, getOriginalPdfUrlNotesById } from "../../../api/list";

// **************** package import
import { jsPDF } from "jspdf";
import PDFViewer from "./notes-docs-viewer";

// ***************** icon
import { TbFileDownload } from "react-icons/tb";
import { IoReloadOutline } from "react-icons/io5";
import { IoIosArrowDown } from "react-icons/io";
import Skeleton from "react-loading-skeleton";
import { ReactSelectInput } from "../../../components/select/react-select";

// *************** utils import
import { WordTitle } from "../../../utils/commonFunction";

// *************** components import
import { CustomButton } from "../../../components/buttons/button";
import { toast } from "react-toastify";

interface NoteDetail {
  [key: string]: any;
}

const Index = () => {
  const { id } = useParams();
  const [donwloadLoading, setDonwloadLoading] = useState<boolean>(false)
  const [noteDetail, setNoteDetail] = useState<NoteDetail>({})
  const [loader, setLoader] = useState<Boolean>(true);
  const [showNotesInfo, setShowNotesInfo] = useState<boolean>(true)

  // pdf modal
  const [showPdfModal, setShowPdfModal] = useState<boolean>(false)
  const [loaderShowOriginalPdfButton, setLoaderShowOriginalPdfButton] = useState<boolean>(false);
  const [hasOriginalPdf, setHasOriginalPdf] = useState<boolean>(false);
  const [urlOriginalPdf, setUrlOriginalPdf] = useState<string | null>(null);
  const [originalPdfValid, setOriginalPdfValid] = useState<boolean>(false);



  const getNotesApiDetails = (id: string | number) => {

    getNotesById(id).then((res: any) => {
      if (res?.result?.data) {
        setNoteDetail(res?.result?.data)
        setHasOriginalPdf(res?.result?.data?.is_exists_source_document || false)
      }
      setLoader(false)
    });
  };

  useEffect(() => {
    if (id) {
      getNotesApiDetails(id);  // Proceed only if id is defined
    }
  }, [id])

  const downloadPdf = () => {
    setDonwloadLoading(true)
    const lines = noteDetail.notes ? noteDetail.notes.split('<br>') : [];

    // Initialize jsPDF instance
    const doc = new jsPDF({
      unit: 'mm',
      format: 'a4',
      orientation: 'portrait'
    });

    // Set margins
    const marginTop = 10;
    const marginLeft = 10;
    const lineHeight = 7;
    const maxLinesPerPage = 40;

    let currentLine = marginTop;
    doc.setFontSize(10);
    // Loop through lines and add them to the PDF
    lines.forEach((line: any, index: any) => {
      if (index > 0 && index % maxLinesPerPage === 0) {
        doc.addPage(); // Add a new page every 40 lines
        currentLine = marginTop; // Reset the line position for new page
      }

      // Add the line to the PDF
      doc.text(line, marginLeft, currentLine);
      currentLine += lineHeight; // Move down for next line
    });
    let pdf_file_name = `${noteDetail.mrn || ''}_${noteDetail.note_type || ''}_note_pdf`
    pdf_file_name = pdf_file_name.replace(/[^a-zA-Z0-9]/g, '_');
    doc.save(pdf_file_name);
    setTimeout(() => {
      setDonwloadLoading(false);
    }, 2000);
  };

  const handleShowOriginalPdf = async () => {
    setLoaderShowOriginalPdfButton(true);
    if (urlOriginalPdf && originalPdfValid) {
      setShowPdfModal(true)
      setLoaderShowOriginalPdfButton(false);
      return
    } else {
      await getOriginalPdfUrlNotesById(id).then((res: any) => {
        if (res?.result?.data) {
          if (res?.result?.data?.is_exists) {
            const source_document_url = res?.result?.data?.source_document_url
            const expiration_minutes = res?.result?.data?.expiration_minutes
            setUrlOriginalPdf(source_document_url)
            setOriginalPdfValid(true)
            setTimeout(() => {
              setOriginalPdfValid(false)
            }, expiration_minutes * 60 * 1000); // expiration_minutes minutes
          } else {
            setHasOriginalPdf(false)
            setUrlOriginalPdf(null)
            toast.error("Original PDF not found")
          }
          setShowPdfModal(true)
        }
        setLoaderShowOriginalPdfButton(false)
      });
    }
  }

  // status colour
  const getStatusClass = (status: string): string => {
    switch (status.toLowerCase()) {
      case "pending":
        return "bg-slate-100 border-slate-500 text-slate-800";
      case "removed":
        return "bg-yellow-100 border-yellow-500 text-yellow-800";
      case "completed":
        return "bg-green-100 border-green-500 text-green-800";
      case "updated":
        return "bg-blue-100 border-blue-500 text-blue-800";
      case "error":
        return "bg-red-100 border-red-500 text-red-800";
      case "sent":
        return "bg-purple-100 border-purple-500 text-purple-800";
      default:
        return "bg-gray-100 border-gray-500 text-gray-800";
    }
  };

  interface StatusCellProps {
    status?: string;
  }

  const StatusCell: React.FC<StatusCellProps> = ({ status }) => {
    return (
      <div
        className={`truncate cursor-default px-1.5 py-1 rounded-lg text-sm text-center font-medium border w-[100px] ${getStatusClass(
          WordTitle(status) || ""
        )}`}
      >
        {WordTitle(status) || "Unknown"}
      </div>
    );
  };


  return (
    <div className="w-full p-[10px] pl-[2px] pr-[2px]">
      <div className="border border-[#EEEFF2] p-[16px] bg-white rounded-[12px]">
        <div className="flex justify-between items-center mb-[10px]">
          <div>
            <h1 className="text-[18px] text-[#1C0F03] font-[500]">
              Notes Info
            </h1>
          </div>
          <div>
            <button onClick={() => setShowNotesInfo(!showNotesInfo)} className={`${showNotesInfo ? '' : 'rotate-90'} me-2`}>
              {showNotesInfo ? (
                <IoIosArrowDown fontSize={22} />
              ) : (
                <IoIosArrowDown fontSize={22} />
              )}
            </button>
          </div>
        </div>
        <hr className="mb-[12px]" />
        {showNotesInfo && <div className={`transition-all duration-900 ease-in-out transform ${showNotesInfo ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none absolute'
          }`}>
          {loader ? (
            <Skeleton width="100%" height="65px" />
          ) : (
            <div className="grid grid-cols-[12%_33%_12%_auto] gap-1">
              <div>Patient name :</div>
              <div>
                {noteDetail.patient_detail?.patient_id ? (
                  <a
                    href={`/patient-lookup/${noteDetail?.patient_detail?.patient_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline text-blue-500"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    {![null, ""].includes(noteDetail.patient_last_name) &&
                      ![null, ""].includes(noteDetail.patient_first_name)
                      ? `${WordTitle(noteDetail.patient_last_name || '')}, ${WordTitle(noteDetail.patient_first_name || '')}`
                      : (WordTitle(noteDetail.patient_last_name || '') || WordTitle(noteDetail.patient_first_name || ''))}
                  </a>
                ) : (
                  <div>
                    {![null, ""].includes(noteDetail.patient_last_name) &&
                      ![null, ""].includes(noteDetail.patient_first_name)
                      ? `${WordTitle(noteDetail.patient_last_name || '')}, ${WordTitle(noteDetail.patient_first_name || '')}`
                      : (WordTitle(noteDetail.patient_last_name || '') || WordTitle(noteDetail.patient_first_name || ''))}
                  </div>
                )}
              </div>
              <div>Date of birth :</div>
              <div>{noteDetail?.DOB ? noteDetail?.DOB : ''}</div>
              <div>Provider name :</div>
              <div>{noteDetail?.provider_name ? WordTitle(noteDetail?.provider_name) : ''}</div>
              <div>Sender name :</div>
              <div>{noteDetail?.sender_name ? WordTitle(noteDetail?.sender_name) : ''}</div>
              <div>Date of service :</div>
              <div>{noteDetail?.encounter_date || ''}</div>
              <div>Location :</div>
              <div>{noteDetail?.location ? WordTitle(noteDetail?.location) : ''}</div>
              <div>Status :</div>
              <div><StatusCell status={noteDetail?.status} /></div>
              {['error', 'updated'].includes((noteDetail?.status || '').toLowerCase()) && <>
                <div>Error :</div>
                <div>{noteDetail?.error || ''}</div>
              </>}
              {['completed'].includes((noteDetail?.status || '').toLowerCase()) && ![null, ""].includes(noteDetail?.description) && <>
                <div>{noteDetail?.is_system_corrected ? 'System' : 'User'} corrected :</div>
                <div className="" title={noteDetail?.description || ''}>{noteDetail?.description || ''}</div>
              </>}
              {/* noteDetail?.sending_efax  */}
              {noteDetail?.is_efax_location === true && (
                <>
                  <div>eFax sending :</div>
                  <div>{noteDetail?.send_efax_number || '+123456789'}</div>
                </>
              )}
            </div>
          )}
          <hr className="my-[12px]" />
        </div>}
        <div className="flex justify-between items-center mb-[10px]">
          <div>
            <h1 className="text-[18px] text-[#1C0F03] font-[500]">
              Notes Details
            </h1>
          </div>
          <div className="flex items-center justify-end gap-[10px]">
            {hasOriginalPdf && <CustomButton
              buttonStyle="text-white !bg-[#215564] px-[26px] py-[12px] text-[14px] font-semibold leading-[17px] border-[#E8ECF4] "
              buttonName="Source PDF"
              primary={false}
              disable={loaderShowOriginalPdfButton}
              onClick={handleShowOriginalPdf}
            // onClick={() => setShowPdfModal(true)}
            />}
            {donwloadLoading ? (
              <div className="rounded-lg p-2 bg-[#215564] text-white" >
                <IoReloadOutline fontSize={22} style={{ animation: 'spin 2s linear infinite' }} />
              </div>
            ) : (<div className={`rounded-lg p-2 ${noteDetail?.notes ? 'bg-[#215564] text-white cursor-pointer' : 'bg-gray-500 text-white cursor-not-allowed'}`} onClick={noteDetail?.notes ? downloadPdf : undefined}>
              <TbFileDownload fontSize={22} />
            </div>)
            }
          </div>
        </div>
        <hr className="mb-[12px]" />
        <div
          className={`overflow-y-auto ${showNotesInfo ? 'h-[46vh]' : 'h-[65vh]'}`}
          style={{
            scrollbarWidth: 'none', // Firefox
            msOverflowStyle: 'none', // IE 10+
          }}
        >
          {loader ? (
            <Skeleton height="99%" className="rounded-lg" />
          ) :  noteDetail.notes ? (
            <div
              dangerouslySetInnerHTML={{
                __html: noteDetail.notes,
              }}
            />
          ) : (
            <div className="text-center text-gray-500 py-10">
              No notes available
            </div>
          )}
        </div>

      </div>
      {showPdfModal &&
        <PDFViewer
          pdfUrl={urlOriginalPdf || ''}
          // pdfUrl={noteDetail?.source_document}
          // pdfUrl="https://arxiv.org/pdf/1708.08021.pdf"
          title="Source PDF"
          showModal={showPdfModal}
          setShowModal={setShowPdfModal}
        />
      }
    </div>
  );
};

export default Index;
